import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  getConversation,
  getConversationByUserId,
  getLatestConversation,
  deleteConversationById,
  updateConversation,
  buildConversationStatusUpdatePayload,
} from "../../core/services/conversation.service";
import { getUserInfoFromToken } from "../../core/utils/authUtils";
import { messageService } from "../../core/services/message.service";
import { useAlert } from "./AlertContext";
import { useClient } from "./ClientContext";
import { useAppState } from "./AppStateContext";
import { getAllActiveSilos } from "../../core/services/silos.service";
import { parseInsightsPayload, parseFaqs, extractParquetFileName } from "../../core/utils/analyzeInsights";
import { API_ERROR_MESSAGES } from "../../core/utils/apiErrorUtils";

const ChatContext = createContext(null);
const toBool = (value) => value === true || value === "true" || value === 1 || value === "1";
const isFileLike = (value) =>
  typeof Blob !== "undefined" && value != null && value instanceof Blob;

const getSessionConversationId = (session) =>
  session?.conversationsId ??
  session?.conversationId ??
  session?.ConversationsId ??
  session?.ConversationId ??
  session?.conversations_id ??
  session?.conversation_id ??
  session?.id ??
  null;

const sameConversation = (session, targetId) =>
  String(getSessionConversationId(session) ?? "") === String(targetId ?? "");

export function useChat() {
  const context = useContext(ChatContext);
  return context;
}

export function ChatProvider({ children }) {
  const { selectedClient, selectedProject } = useClient();
  const { showAlert, setAlert } = useAlert();
  const { useWebSearch } = useAppState();

  const [messages, setMessages] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [currentChatSession, setCurrentChatSession] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [defaultConversationsBySilo, setDefaultConversationsBySilo] = useState({});
  const [privateConversations, setPrivateConversations] = useState([]);
  const [defaultPrivateConversationsBySilo, setDefaultPrivateConversationsBySilo] = useState({});
  const [chatType, setChatType] = useState("global");
  const [selectedSilo, setSelectedSilo] = useState(null);
  const [selectedSiloIsDefault, setSelectedSiloIsDefault] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingPrivateConversations, setLoadingPrivateConversations] = useState(false);
  const [deletingConversationIds, setDeletingConversationIds] = useState({});
  const [renamingConversationIds, setRenamingConversationIds] = useState({});

  const [conversationContext, setConversationContext] = useState({
    lastEntity: null,
    lastTopic: null,
    messageHistory: [],
  });

  const [loading, setLoading] = useState(false);
  const streamAbortControllerRef = useRef(null);
  const selectedClientId =
    selectedClient?.clientId ?? selectedClient?.Id ?? selectedClient?.id ?? null;
  const selectedProjectId =
    selectedProject?.projectId ?? selectedProject?.Id ?? selectedProject?.id ?? null;

  const getAuthenticatedUserId = () => {
    const token =
      typeof window !== "undefined" ? localStorage.getItem("authToken") : null;
    return getUserInfoFromToken(token)?.id || null;
  };

  const fetchConversationsListForSilo = async (silosId, { isDefaultSelected = false } = {}) => {
    if (selectedClientId) {
      return getConversation(silosId, {
        isGlobal: !isDefaultSelected,
        clientId: selectedClientId,
        projectId: selectedProjectId,
      });
    }
    const userId = getAuthenticatedUserId();
    if (!userId) {
      throw new Error("Authentication required to load conversations.");
    }
    return getConversationByUserId(silosId);
  };

  const fetchConversationsBySilo = async (silosId, options = {}) => {
    if (!silosId) return;

    // Only reset dropdown when user picks a silo (ModeButtons). After send/stream
    // refetch must not flip Private → Global.
    if (options.resetChatType) {
      setChatType("global");
    }

    setLoadingConversations(true);
    try {
      const resolvedIsDefault = typeof options.isDefaultSelected === "boolean"
        ? options.isDefaultSelected
        : toBool(options.defaultSelected);
      const response = await fetchConversationsListForSilo(silosId, {
        isDefaultSelected: resolvedIsDefault,
      });
      const conversationData = response.data || [];
      if (resolvedIsDefault) {
        setDefaultConversationsBySilo((prev) => ({
          ...prev,
          [silosId]: conversationData,
        }));
        // Also store in main conversations state for consistency
        setConversations(conversationData);
      } else {
        setConversations(conversationData);
      }
      setSelectedSilo(silosId);
      setSelectedSiloIsDefault(resolvedIsDefault);
    } catch (error) {
      const resolvedIsDefault = typeof options.isDefaultSelected === "boolean"
        ? options.isDefaultSelected
        : toBool(options.defaultSelected);
      if (resolvedIsDefault) {
        setDefaultConversationsBySilo((prev) => ({
          ...prev,
          [silosId]: [],
        }));
        // Also store in main conversations state for consistency
        setConversations([]);
      } else {
        setConversations([]);
      }
    } finally {
      setLoadingConversations(false);
    }
  };

  const fetchPrivateConversationsByClient = useCallback(async () => {
    if (!selectedSilo) {
      setPrivateConversations([]);
      return;
    }

    setLoadingPrivateConversations(true);
    try {
      let response;
      if (selectedClientId) {
        response = await getConversation(selectedSilo, {
          isGlobal: false,
          clientId: selectedClientId,
          projectId: selectedProjectId,
        });
      } else {
      const userId = getAuthenticatedUserId();
      if (!userId) {
          throw new Error("Authentication required to load conversations.");
        }
       response = await getConversationByUserId(selectedSilo);
      }
      const privateData = (response.data || []).map((conversation) => ({
        ...conversation,
        globallyAvailable: false,
      }));
      if (selectedSiloIsDefault) {
        setDefaultPrivateConversationsBySilo((prev) => ({
          ...prev,
          [selectedSilo]: privateData,
        }));
        setPrivateConversations(privateData);
      } else {
        setPrivateConversations(privateData);
      }
    } catch (error) {
      if (selectedSiloIsDefault) {
        setDefaultPrivateConversationsBySilo((prev) => ({
          ...prev,
          [selectedSilo]: [],
        }));
        setPrivateConversations([]);
      } else {
        setPrivateConversations([]);
      }
    } finally {
      setLoadingPrivateConversations(false);
    }
  }, [selectedSilo, selectedSiloIsDefault, selectedClientId, selectedProjectId]);

  const fetchGlobalConversationsByClient = useCallback(async (clientId = selectedClientId) => {
    if (selectedSiloIsDefault && selectedSilo) {
      await fetchConversationsBySilo(selectedSilo, { isDefaultSelected: true });
      return;
    }

    if (!clientId || !selectedSilo) {
      setConversations([]);
      return;
    }

    setLoadingConversations(true);
    try {
      const response = await getConversation(selectedSilo, {
        isGlobal: true,
        clientId,
        projectId: selectedProjectId,
      });
      const globalConversations = (response.data || []).map((conversation) => ({
        ...conversation,
        globallyAvailable: true,
      }));
      setConversations(globalConversations);
    } catch (error) {
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  }, [selectedClientId, selectedSilo, selectedSiloIsDefault, selectedProjectId]);

  const handleChatTypeChange = async (type) => {
    setChatType(type);
    if (type === "private") {
      await fetchPrivateConversationsByClient();
      return;
    }
    await fetchGlobalConversationsByClient();
  };

  const clearConversations = () => {
    setConversations([]);
    setPrivateConversations([]);
    setSelectedSilo(null);
    setSelectedSiloIsDefault(false);
  };

  const clearActiveChatSession = () => {
    setCurrentChatSession(null);
    setMessages([]);
    setConversationContext({
      lastEntity: null,
      lastTopic: null,
      messageHistory: [],
    });
  };

  const resetToDefaultHomeSilo = async () => {
    clearConversations();
    try {
      const response = await getAllActiveSilos();
      const visibleSilos = (response.data || []).filter((silo) => {
        const hiddenFlag = silo.isHide ?? silo.ishide ?? silo.is_hidden;
        return !toBool(hiddenFlag);
      });
      const defaultSilo = visibleSilos.find((silo) =>
        toBool(silo.defaultSelected ?? silo.defaultselected),
      );
      if (defaultSilo?.silosId) {
        await fetchConversationsBySilo(defaultSilo.silosId, {
          isDefaultSelected: true,
        });
      }
    } catch {
      // Keep cleared state if silos API fails.
    }
  };

  
  useEffect(() => {
    if (selectedClientId && chatSessions.length === 0 && !currentChatSession) {
      createNewChat(selectedClient);
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (selectedClientId && chatType === "global" && selectedSilo) {
      fetchGlobalConversationsByClient();
    }
  }, [selectedClientId, chatType, selectedSilo, fetchGlobalConversationsByClient]);

  useEffect(() => {
    if (chatType === "private" && selectedSilo) {
      fetchPrivateConversationsByClient();
    }
  }, [chatType, selectedSilo, selectedClientId, selectedProjectId, fetchPrivateConversationsByClient]);

  // Default (home) silo: refetch when silo or client changes.
  // With a client: getConversation?isGlobal=false. Without: getConversationByUserId.
  // Not toggled by global/private chat type.
  useEffect(() => {
    if (!selectedSiloIsDefault || !selectedSilo) return;
    fetchConversationsBySilo(selectedSilo, { isDefaultSelected: true });
  }, [selectedSiloIsDefault, selectedSilo, selectedClientId, selectedProjectId]);

  const effectiveGlobalConversations = selectedSiloIsDefault
    ? (defaultConversationsBySilo[selectedSilo] || [])
    : conversations;
  const effectivePrivateConversations = selectedSiloIsDefault
    ? (defaultPrivateConversationsBySilo[selectedSilo] || [])
    : privateConversations;

const formatContent = (text) => {
  if (!text) return "";
  
  return String(text).replace(/\r/g, "");
};

const extractConversationId = (responseData) => {
  if (!responseData) return null;

  const directId =
    responseData?.conversationsId ||
    responseData?.conversationId ||
    responseData?.id;
  if (directId) return directId;

  if (Array.isArray(responseData) && responseData.length > 0) {
    const first = responseData[0];
    return first?.conversationsId || first?.conversationId || first?.id || null;
  }

  if (responseData?.data) {
    return extractConversationId(responseData.data);
  }

  return null;
};

const parseMaybeJson = (value) => {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const toStringArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).filter(Boolean);
  }
  if (typeof value === "string") {
    const parsed = parseMaybeJson(value);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item)).filter(Boolean);
    }
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (value == null) return [];
  return [String(value)];
};

const getAnalyzePayload = (message) => {
  const directPayload = parseMaybeJson(message?.analyzeResponse);
  if (!directPayload) return null;

  // Support payloads where analyzeResponse is wrapped inside another object.
  const nestedPayload = parseMaybeJson(directPayload?.analyzeResponse);
  return nestedPayload || directPayload;
};

const buildAnalyzeMetaFromMessage = (message) => {
  const analyzePayload = getAnalyzePayload(message);
  const insights = parseInsightsPayload(message);
  const faqs = parseFaqs(message);
  const payload =
    analyzePayload && typeof analyzePayload === "object" ? analyzePayload : {};

  const successRows = Array.isArray(payload?.success) ? payload.success : [];
  const charts = toStringArray(payload?.Possible_charts);
  const rawYAxis = payload?.["Y-axis"];
  const yAxis = Array.isArray(rawYAxis) ? rawYAxis[0] ?? "" : (rawYAxis ?? "");
  const parquetFileName =
    extractParquetFileName(payload) || extractParquetFileName(message);

  const hasPersistedAnalyzeData =
    successRows.length > 0 ||
    charts.length > 0 ||
    !!payload?.Title ||
    !!payload?.preface ||
    !!insights ||
    faqs.length > 0;

  if (!hasPersistedAnalyzeData) return null;

  return {
    preface: payload?.preface ?? "",
    title: payload?.Title ?? "",
    xAxis: payload?.["X-axis"] ?? "",
    yAxis,
    data: successRows,
    charts,
    table: successRows,
    insights: insights ?? null,
    faqs,
    fileName: parquetFileName,
    displayFileName: /\.(csv|xlsx|xls|parquet)$/i.test(
      String(message?.contentText ?? "").trim(),
    )
      ? String(message.contentText).trim()
      : "",
  };
};

const buildSchemaMetaFromMessage = (message) => {
  const analyzePayload = getAnalyzePayload(message);
  const dataTypes = analyzePayload?.success?.[0]?.Data_Types;
  if (!dataTypes || typeof dataTypes !== "object") return null;

  const columns = Object.entries(dataTypes).map(([name, type]) => ({
    name,
    type,
    originalName: name,
    detectedType: type,
  }));

  if (columns.length === 0) return null;

  const persistedConversationId =
    message?.conversationId ??
    message?.conversationsId ??
    message?.ConversationId ??
    message?.ConversationsId ??
    null;

  const resolvedFileName =
    message?.fileName ??
    message?.filename ??
    message?.file_name ??
    analyzePayload?.fileName ??
    analyzePayload?.filename ??
    analyzePayload?.file_name ??
    analyzePayload?.parquet_file_name ??
    analyzePayload?.parquetFileName ??
    "";

  return {
    schema: { columns },
    userSessionFileId: persistedConversationId,
    fileName: String(resolvedFileName || "").trim(),
  };
};

const buildMessageMeta = (message) => {
  const analyzeMeta = buildAnalyzeMetaFromMessage(message);
  const schemaMeta = buildSchemaMetaFromMessage(message);

  if (!analyzeMeta && !schemaMeta) return null;
  return {
    ...(analyzeMeta || {}),
    ...(schemaMeta || {}),
  };
};

const addAttachmentName = (names, value) => {
  const name = String(value ?? "").trim();
  if (!name || name === "[object Object]" || names.includes(name)) return;
  names.push(name);
};

const collectAttachmentNames = (names, value) => {
  if (value == null || value === "") return;

  if (typeof value === "string") {
    const parsed = parseMaybeJson(value);
    if (parsed != null && typeof parsed !== "string") {
      collectAttachmentNames(names, parsed);
      return;
    }
    addAttachmentName(names, value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectAttachmentNames(names, item));
    return;
  }

  if (typeof value === "object") {
    addAttachmentName(
      names,
      value.name ??
        value.fileName ??
        value.FileName ??
        value.attachmentName ??
        value.attachmentFileName,
    );
  }
};

const resolveAttachmentNames = (msg) => {
  const names = [];
  addAttachmentName(names, msg?.attachmentName);
  addAttachmentName(names, msg?.attachmentFileName);
  collectAttachmentNames(names, msg?.attachment);
  collectAttachmentNames(names, msg?.attachments);
  addAttachmentName(names, msg?.fileName);
  addAttachmentName(names, msg?.FileName);
  return names;
};

const toDisplayMessage = (msg) => {
  const persistedMessageId =
    msg.messagesId ?? msg.messageId ?? msg.MessagesId ?? msg.MessageId ?? msg.id ?? null;
  const persistedConversationId =
    msg.conversationId ??
    msg.conversationsId ??
    msg.ConversationId ??
    msg.ConversationsId ??
    null;

  const lockValue =
    msg.isLock ??
    msg.islock ??
    msg.IsLock ??
    msg.is_lock ??
    msg.lock ??
    msg.Lock ??
    false;

  const attachmentNames = resolveAttachmentNames(msg);

  return {
    id: persistedMessageId,
    messagesId: persistedMessageId,
    conversationId: persistedConversationId,
    conversationsId: persistedConversationId,
    role: msg.role?.toLowerCase() === "assistant" ? "ai" : "user",
    content: formatContent(msg.contentText || ""),
    displayContent: formatContent(msg.contentText || ""),
    text: formatContent(msg.contentText || ""),
    meta: buildMessageMeta(msg),
    rating: msg.rating ?? null,
    comment: msg.comment ?? msg.Comment ?? "",
    isLock:
      lockValue === true ||
      lockValue === 1 ||
      lockValue === "true" ||
      lockValue === "1",
    note:
      msg.note ??
      msg.Note ??
      msg.notes ??
      msg.Notes ??
      msg.noteText ??
      msg.NoteText ??
      msg.messageNote ??
      msg.MessageNote ??
      msg.messageNotes ??
      msg.MessageNotes ??
      msg.message_note ??
      null,
    attachmentName: attachmentNames[0] ?? null,
    attachmentNames,
    createdAt: msg.createdAt,
  };
};

  const createNewChat = (clientObj = selectedClient) => {
    // Default home silo supports chats without selecting a client.
    if (!clientObj && !selectedSiloIsDefault) return;
    const targetClientId = clientObj?.clientId ?? clientObj?.Id ?? clientObj?.id ?? null;

    const existingEmptyChat = chatSessions.find(
      (s) =>
        String(s.clientId ?? "") === String(targetClientId ?? "") &&
        s.messages.length === 0 &&
        s.title === "New Chat"
    );

    if (existingEmptyChat) {
      setCurrentChatSession(existingEmptyChat);
      setMessages([]);
      return existingEmptyChat;
    }


    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    const counter = chatSessions.length;
    const uniqueId = `chat-${timestamp}-${random}-${counter}`;

    const newSession = {
      id: uniqueId,
      clientId: targetClientId,
      chatType,
      title: "New Chat",
      date: new Date().toISOString(),
      messages: [],
      context: {
        lastEntity: null,
        lastTopic: null,
        messageHistory: [],
      },
    };

    setChatSessions((prev) => [newSession, ...prev]);
    setCurrentChatSession(newSession);
    setMessages([]);

    setConversationContext({
      lastEntity: null,
      lastTopic: null,
      messageHistory: [],
    });
    return newSession;
  };

const loadChatSession = async (session) => {
  if (loading) return;
  if (!session) return;

  const conversationId = getSessionConversationId(session);
  const normalizedSession = conversationId
    ? { ...session, conversationsId: conversationId, conversationId }
    : session;

  setCurrentChatSession(normalizedSession);

  if (conversationId) {
    try {
      const messageResponse = await messageService.getMessagesByConversationId(conversationId);
      const formattedMessages = (messageResponse || [])
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        .map(toDisplayMessage);
      setMessages(formattedMessages);
      setCurrentChatSession((prev) =>
        sameConversation(prev, conversationId)
          ? { ...prev, messages: formattedMessages }
          : prev,
      );
      return;
    } catch (error) {
      const fallbackMessages = normalizedSession.messages || [];
      setMessages(fallbackMessages);
      return;
    }
  }

  setMessages(normalizedSession.messages || []);
};


  const deleteChatSession = (id, options = {}) => {
    let session = chatSessions.find((s) => s.id === id);
    
    if (!session && selectedSilo && chatType === "global") {
      session = effectiveGlobalConversations.find((s) => sameConversation(s, id));
    }
    if (!session && chatType === "private") {
      session = effectivePrivateConversations.find((s) => sameConversation(s, id));
    }
    
    if (!session) return;

    const rawSessionTitle = session.title || `Conversation ${session.id?.slice(-6) || "Unknown"}`;
    const sessionTitle =
      String(rawSessionTitle).length > 20
        ? `${String(rawSessionTitle).slice(0, 20).trim()}...`
        : rawSessionTitle;
    const conversationId = getSessionConversationId(session);
    const hasPersistedConversationId =
      !!conversationId && !String(conversationId).startsWith("chat-");

    const removeConversationLocally = (targetId) => {
      if (chatType === "private") {
        if (selectedSiloIsDefault) {
          setDefaultPrivateConversationsBySilo((prev) => ({
            ...prev,
            [selectedSilo]: (prev[selectedSilo] || []).filter(
              (s) => !sameConversation(s, targetId),
            ),
          }));
        } else {
          setPrivateConversations((prev) =>
            prev.filter((s) => !sameConversation(s, targetId)),
          );
        }
      } else if (selectedSilo) {
        if (selectedSiloIsDefault) {
          setDefaultConversationsBySilo((prev) => ({
            ...prev,
            [selectedSilo]: (prev[selectedSilo] || []).filter(
              (s) => !sameConversation(s, targetId),
            ),
          }));
        } else {
          setConversations((prev) => prev.filter((s) => !sameConversation(s, targetId)));
        }
      }

      setChatSessions((prev) => prev.filter((s) => !sameConversation(s, targetId)));

      if (sameConversation(currentChatSession, targetId)) {
        setCurrentChatSession(null);
        setMessages([]);
      }
    };

    const runDelete = async () => {
      const deleteKey = String(conversationId ?? id);
      setDeletingConversationIds((prev) => ({ ...prev, [deleteKey]: true }));
      try {
        if (hasPersistedConversationId) {
          await deleteConversationById(conversationId);
        }
        removeConversationLocally(conversationId ?? id);
      } catch (error) {
        showAlert("Delete Failed", error?.message || "Unable to delete this conversation.");
      } finally {
        setDeletingConversationIds((prev) => {
          const next = { ...prev };
          delete next[deleteKey];
          return next;
        });
      }
    };

    if (options.skipConfirm) {
      runDelete();
      return;
    }

    setAlert({
      open: true,
      title: "Delete Chat?",
      message: `Are you sure you want to delete "${sessionTitle}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onCancel: () => {},
      onConfirm: runDelete,
    });
  };

  const applyConversationTitleLocally = (conversationId, title) => {
    const trimmed = String(title ?? "").trim();
    if (!conversationId || !trimmed) return;
    const patchRow = (s) =>
      sameConversation(s, conversationId)
        ? { ...s, title: trimmed, silosName: trimmed }
        : s;

    setConversations((prev) => (Array.isArray(prev) ? prev.map(patchRow) : prev));
    setPrivateConversations((prev) => (Array.isArray(prev) ? prev.map(patchRow) : prev));
    setDefaultConversationsBySilo((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (Array.isArray(next[k])) next[k] = next[k].map(patchRow);
      }
      return next;
    });
    setDefaultPrivateConversationsBySilo((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (Array.isArray(next[k])) next[k] = next[k].map(patchRow);
      }
      return next;
    });
    setChatSessions((prev) => prev.map(patchRow));
    setCurrentChatSession((prev) =>
      prev && sameConversation(prev, conversationId) ? patchRow(prev) : prev,
    );
  };

  const updateSessionTitleByConversationId = (conversationId, title) => {
    applyConversationTitleLocally(conversationId, title);
  };

  const renameChatSession = async (session, newTitle) => {
    const conversationId = getSessionConversationId(session);
    const trimmed = String(newTitle ?? "").trim();

    if (!conversationId || String(conversationId).startsWith("chat-")) {
      showAlert("Rename failed", "This conversation cannot be renamed yet.");
      return false;
    }
    if (!trimmed) {
      showAlert("Invalid title", "Please enter a title.");
      return false;
    }

    const token =
      typeof window !== "undefined" ? localStorage.getItem("authToken") : null;
    const userId = getUserInfoFromToken(token)?.id || "";

    const { body } = buildConversationStatusUpdatePayload(session, trimmed, {
      silosId: selectedSilo,
      modelId: selectedProject?.modelId ?? selectedProject?.ModelId ?? 1,
      userId,
      globallyAvailable: selectedSiloIsDefault ? false : chatType !== "private",
    });

    const renameKey = String(conversationId);
    setRenamingConversationIds((prev) => ({ ...prev, [renameKey]: true }));
    try {
      await updateConversation(conversationId, body);
      applyConversationTitleLocally(conversationId, trimmed);
      return true;
    } catch (error) {
      const detail =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message;
      showAlert("Rename failed", detail || "Unable to rename this conversation.");
      return false;
    } finally {
      setRenamingConversationIds((prev) => {
        const next = { ...prev };
        delete next[renameKey];
        return next;
      });
    }
  };

 
  const addMessage = (role, content, extra = {}) => {
    const msg = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role,
      content,
      displayContent: content,
      ...extra,
    };

    setMessages((prev) => [...prev, msg]);
    return msg.id;
  };

 
  const sendQuery = async (_client, query, opts = {}) => {
    const normalizedQuery = String(query || "").trim();
    const attachment = isFileLike(opts?.attachment) ? opts.attachment : null;
    if (!normalizedQuery && !attachment) return;

    const requiresClientSelection = !selectedSiloIsDefault;
    if (requiresClientSelection && !selectedClientId) {
      showAlert("Client Required", "Please select a client for this silo.");
      return;
    }

    const activeSession = currentChatSession || createNewChat(selectedClient);
    const activeSessionId = activeSession?.id || null;

    setLoading(true);
    const streamController = new AbortController();
    streamAbortControllerRef.current = streamController;

    addMessage(
      "user",
      normalizedQuery,
      attachment
        ? {
            attachmentName: attachment.name,
            attachmentNames: [attachment.name],
          }
        : {},
    );

    if (activeSession && activeSession.title === "New Chat") {
      const titleSource = normalizedQuery || attachment?.name || "New Chat";
      const title =
        titleSource.length > 40
          ? titleSource.substring(0, 40).trim() + "..."
          : titleSource;

      const updatedSession = { ...activeSession, title };
      setCurrentChatSession(updatedSession);
      
      setChatSessions((prev) =>
        prev.map((s) =>
          s.id === activeSession.id ? updatedSession : s
        )
      );
    }

    const aiMsgId = addMessage("ai", "");

    let fullResponse = "";
    let responseFormat = "structured";
    let resolvedConversationId = activeSession?.conversationsId || null;

    try {
      const siloId = selectedSilo;
      if (!siloId) {
        throw new Error("Please select a silo before sending a message.");
      }
      if (resolvedConversationId && activeSessionId) {
        setCurrentChatSession((prev) =>
          prev?.id === activeSessionId
            ? { ...prev, conversationsId: resolvedConversationId }
            : prev
        );
        setChatSessions((prev) =>
          prev.map((s) =>
            s.id === activeSessionId
              ? { ...s, conversationsId: resolvedConversationId }
              : s
          )
        );
      }

      const isPrivateSearch = chatType === "private";
      const modelId = Number(opts?.modelId ?? 1);
      const payload = {
        conversationId: resolvedConversationId ?? "",
        modelId: Number.isFinite(modelId) && modelId > 0 ? modelId : 1,
        contentText: normalizedQuery,
        silosId: siloId,
        clientId: selectedClientId ?? "",
        projectId: selectedProjectId ?? "",
        use_web_search: !!useWebSearch,
        globallyAvailable: selectedSiloIsDefault ? false : !isPrivateSearch,
        ...(attachment ? { attachment } : {}),
      };

      const refreshConversationLists = async () => {
        if (!selectedSilo) return;
        // Home default-silo history always comes from conversations,
        // regardless of current chatType.
        if (selectedSiloIsDefault) {
          await fetchConversationsBySilo(selectedSilo, {
            isDefaultSelected: true,
          });
          return;
        }
        if (chatType === "private") {
          await fetchPrivateConversationsByClient();
          return;
        }
        await fetchConversationsBySilo(selectedSilo, {
          isDefaultSelected: selectedSiloIsDefault,
        });
      };

      if (selectedSilo) {
        setTimeout(() => {
          refreshConversationLists();
        }, 800);
      }

      if (selectedSilo) {
        await refreshConversationLists();
      }

      let rafId = null;

      const schedulePaint = () => {
        // Cancel any pending RAF to prevent stale closures
        if (rafId) {
          window.cancelAnimationFrame(rafId);
        }
        
        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          const formatted = formatContent(fullResponse || "");
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiMsgId
                ? {
                    ...msg,
                    content: formatted,
                    displayContent: formatted,
                    format: responseFormat,
                  }
                : msg
            )
          );
        });
      };

      await messageService.streamMessage(
        payload,
        null,
        (metadata) => {
          if (metadata?.format) {
            responseFormat = metadata.format;
            schedulePaint();
          }
        },
        (delta) => {
          fullResponse += delta || "";
          schedulePaint();
        },
        { signal: streamController.signal },
      );

      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }

      // Ensure final formatting is applied immediately after streaming completes
      schedulePaint();

      if (!resolvedConversationId) {
        const latestConversationResponse = await getLatestConversation(siloId);
        const latestConversationId = extractConversationId(latestConversationResponse?.data);

        if (latestConversationId) {
          resolvedConversationId = latestConversationId;
          if (activeSessionId) {
            setCurrentChatSession((prev) =>
              prev?.id === activeSessionId
                ? { ...prev, conversationsId: latestConversationId }
                : prev
            );
            setChatSessions((prev) =>
              prev.map((s) =>
                s.id === activeSessionId
                  ? { ...s, conversationsId: latestConversationId }
                  : s
              )
            );
          }
        }
      }

     
      if (resolvedConversationId) {
        try {
          const persistedMessages = await messageService.getMessagesByConversationId(
            resolvedConversationId
          );
          const formattedPersistedMessages = (persistedMessages || [])
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
            .map(toDisplayMessage);

          if (formattedPersistedMessages.length > 0) {
            setMessages((currentMessages) => {
              const streamedAIMessage = currentMessages.find(msg => msg.id === aiMsgId);
              const optimisticUserAttachments = currentMessages
                .filter((msg) => msg.role === "user")
                .map((msg) => ({
                  attachmentName: msg.attachmentName ?? null,
                  attachmentNames: Array.isArray(msg.attachmentNames)
                    ? msg.attachmentNames.filter(Boolean)
                    : msg.attachmentName
                      ? [msg.attachmentName]
                      : [],
                }));
              let userMessageIndex = 0;

                return formattedPersistedMessages.map((persistedMsg, index) => {
                let next = persistedMsg;

                if (persistedMsg.role === "user") {
                  const optimistic = optimisticUserAttachments[userMessageIndex++];
                  const optimisticNames = optimistic?.attachmentNames?.length
                    ? optimistic.attachmentNames
                    : optimistic?.attachmentName
                      ? [optimistic.attachmentName]
                      : [];
                  if (optimisticNames.length > 0) {
                    next = {
                      ...next,
                      attachmentName: optimisticNames[0],
                      attachmentNames: optimisticNames,
                    };
                  }
                }

                if (
                  streamedAIMessage &&
                  index === formattedPersistedMessages.length - 1 &&
                  persistedMsg.role === "ai"
                ) {
                  next = {
                    ...next,
                    content: streamedAIMessage.content ?? next.content,
                    displayContent:
                      streamedAIMessage.displayContent ?? next.displayContent,
                    format: streamedAIMessage.format ?? next.format,
                  };
                }

                return next;
              });
            });
            
            setChatSessions((prevSessions) =>
              prevSessions.map((s) => {
                if (s.id !== activeSessionId) return s;
                return { ...s, messages: formattedPersistedMessages };
              })
            );
          }
        } catch (syncError) {
          // Fallback to in-memory messages if refresh fails.
        }
      }

      // Ensure history list reflects the newly created/updated conversation immediately.
      await refreshConversationLists();

      // Persist final messages into the current chat session
      setMessages((currentMessages) => {
        setChatSessions((prevSessions) =>
          prevSessions.map((s) => {
            if (s.id !== activeSessionId) return s;

            const finalMessages = currentMessages.map((msg) => ({
              ...msg,
              displayContent: msg.displayContent ?? msg.content,
            }));

            return { ...s, messages: finalMessages };
          })
        );
        return currentMessages;
      });
      
    } catch (err) {
      if (err?.name === "AbortError") {
        return;
      }
      const rawErrorMessage = String(err?.message || "");
      const displayError =
        err?.category === "server" ||
        Number(err?.status) >= 500 ||
        /networkerror|network error|failed to fetch/i.test(rawErrorMessage)
          ? API_ERROR_MESSAGES.server
          : `Error: ${rawErrorMessage}`;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === aiMsgId
            ? { ...msg, displayContent: displayError }
            : msg
        )
      );
    } finally {
      streamAbortControllerRef.current = null;
      setLoading(false);
    }
  };

  const stopGenerating = useCallback(() => {
    streamAbortControllerRef.current?.abort();
    streamAbortControllerRef.current = null;
    setLoading(false);
  }, []);

return (
  <ChatContext.Provider
    value={{
      messages,
      chatSessions,
      currentChatSession,
      setCurrentChatSession,
      conversations: effectiveGlobalConversations,
      privateConversations: effectivePrivateConversations,
      chatType,
      selectedSilo,
      loadingConversations,
      loadingPrivateConversations,
      deletingConversationIds,
      setChatType: handleChatTypeChange,
      createNewChat,
      loadChatSession,
      deleteChatSession,
      renameChatSession,
      updateSessionTitleByConversationId,
      renamingConversationIds,
      addMessage,
      sendQuery,
      stopGenerating,
      conversationContext,
      setConversationContext,
      loading,
      setMessages,
      fetchConversationsBySilo,
      fetchGlobalConversationsByClient,
      fetchPrivateConversationsByClient,
      clearConversations,
      clearActiveChatSession,
      resetToDefaultHomeSilo,
      selectedSiloIsDefault,
    }}
  >
    {children}
  </ChatContext.Provider>
);
}
